/**
 * Поиск зоны ограничений V2 по ID в тайловом источнике.
 * Ищет в источнике martin.functional_areas_mvt (polygon/outer_boundary слои).
 * Ищет по полям: id (числовой), internal_id (UUID).
 *
 * Возвращает ВСЕ найденные фрагменты зоны из всех тайлов, чтобы
 * подсветить зону целиком и спаниться на её полный экстент.
 *
 * @param {Object} map - Mapbox GL JS map instance
 * @param {string} zoneId - ID зоны (числовой или UUID internal_id)
 * @returns {Object|null} { id, internal_id, type, features } или null если не найдена
 */

/**
 * Вспомогательная: собирает все уникальные фрагменты по полю/значению.
 * Дедупликация по id фичи (tileId может порождать дубли одной фичи).
 */
function queryAllByField (map, sourceId, sourceLayer, field, value) {
    const geomTypes = [ 'polygon', 'outer_boundary' ];
    const seen = new Set();
    const result = [];
    for (const geomType of geomTypes) {
        const hits = map.querySourceFeatures(sourceId, {
            sourceLayer,
            filter: [
                'all',
                [ '==', [ 'to-string', [ 'get', field ] ], String(value) ],
                [ '==', [ 'get', 'geom_type' ], geomType ],
            ],
        });
        for (const f of hits) {
            // Mapbox может вернуть одну и ту же фичу из разных тайлов.
            // Дедуплицируем по id (если есть) + geom_type + первая координата.
            const dedupeKey = `${f.properties.id}_${f.properties.geom_type}_${JSON.stringify(
                f.geometry && f.geometry.coordinates ? f.geometry.coordinates[0] : null
            )}`;
            if (!seen.has(dedupeKey)) {
                seen.add(dedupeKey);
                result.push(f);
            }
        }
        // Если нашли polygon-фрагменты — outer_boundary не нужен
        if (result.length > 0 && geomType === 'polygon') break;
    }
    return result;
}

export function searchRestrictionZoneV2InTiles (map, zoneId) {
    if (!map || !zoneId) {
        return null;
    }

    const sourceId = 'martin.functional_areas_mvt2';
    const sourceLayer = 'martin.functional_areas_mvt2';

    try {
        const source = map.getSource(sourceId);
        if (!source) {
            return null;
        }

        // Сначала ищем по числовому id, затем по internal_id (UUID)
        let allFeatures = queryAllByField(map, sourceId, sourceLayer, 'id', zoneId);
        if (allFeatures.length === 0) {
            allFeatures = queryAllByField(map, sourceId, sourceLayer, 'internal_id', zoneId);
        }

        if (allFeatures.length === 0) {
            return null;
        }

        const props = allFeatures[0].properties;

        return {
            id: props.id || zoneId,
            internal_id: props.internal_id || null, // eslint-disable-line camelcase
            type: props.type || null,
            features: allFeatures,
        };
    } catch (err) {
        return null;
    }
}

/**
 * Вычислить bbox [[minLon, minLat], [maxLon, maxLat]] по массиву координат (плоский обход)
 */
function coordsBBox (coordsArray) {
    let minLon = Infinity; let maxLon = -Infinity;
    let minLat = Infinity; let maxLat = -Infinity;
    function walk (c) {
        if (typeof c[0] === 'number') {
            if (c[0] < minLon) minLon = c[0];
            if (c[0] > maxLon) maxLon = c[0];
            if (c[1] < minLat) minLat = c[1];
            if (c[1] > maxLat) maxLat = c[1];
        } else {
            c.forEach(walk);
        }
    }
    walk(coordsArray);
    return [ [ minLon, minLat ], [ maxLon, maxLat ] ];
}

/**
 * Подсветить найденную V2-зону: спаниться на полный экстент всех фрагментов,
 * подсветить их все сразу одним FeatureCollection.
 *
 * @param {Object} map    - Mapbox GL JS map instance
 * @param {Object} result - Результат поиска { id, internal_id, type, features }
 */
export function highlightRestrictionZoneV2 (map, result) {
    if (!map || !result || !result.id) {
        return;
    }

    const features = result.features || [];

    // Вычисляем суммарный bbox по всем фрагментам
    let minLon = Infinity; let maxLon = -Infinity;
    let minLat = Infinity; let maxLat = -Infinity;
    for (const f of features) {
        if (f.geometry && f.geometry.coordinates) {
            const [ [ fMinLon, fMinLat ], [ fMaxLon, fMaxLat ] ] = coordsBBox(f.geometry.coordinates);
            if (fMinLon < minLon) minLon = fMinLon;
            if (fMaxLon > maxLon) maxLon = fMaxLon;
            if (fMinLat < minLat) minLat = fMinLat;
            if (fMaxLat > maxLat) maxLat = fMaxLat;
        }
    }

    const hasBounds = isFinite(minLon) && isFinite(minLat) && isFinite(maxLon) && isFinite(maxLat);

    if (hasBounds) {
        map.fitBounds(
            [ [ minLon, minLat ], [ maxLon, maxLat ] ],
            { padding: 120, duration: 1500, maxZoom: 16 }
        );
    }

    // Рисуем подсветку после окончания анимации камеры
    setTimeout(() => {
        try {
            // Повторно собираем все фрагменты из тайлов (камера уже переехала — тайлы обновились)
            const sourceId = 'martin.functional_areas_mvt2';
            const sourceLayer = 'martin.functional_areas_mvt2';
            const fresh = map.querySourceFeatures(sourceId, {
                sourceLayer,
                filter: [
                    'all',
                    [ '==', [ 'to-string', [ 'get', 'id' ] ], String(result.id) ],
                    [ '==', [ 'get', 'geom_type' ], 'polygon' ],
                ],
            });
            const toRender = fresh.length > 0 ? fresh : features;
            if (toRender.length === 0) return;

            const tempSourceId = 'temp-v2-blink-source';
            const tempLayerId = 'temp-v2-blink-layer';

            if (map.getLayer(tempLayerId)) map.removeLayer(tempLayerId);
            if (map.getSource(tempSourceId)) map.removeSource(tempSourceId);

            map.addSource(tempSourceId, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: toRender.map(f => ({
                        type: 'Feature',
                        geometry: f.geometry,
                        properties: f.properties,
                    })),
                },
            });

            map.addLayer({
                id: tempLayerId,
                type: 'line',
                source: tempSourceId,
                paint: {
                    'line-color': '#ff0000',
                    'line-width': 6,
                    'line-opacity': 1,
                },
            });

            let blinkCount = 0;
            const blinkTimer = setInterval(() => {
                if (blinkCount >= 6) {
                    clearInterval(blinkTimer);
                    if (map.getLayer(tempLayerId)) map.removeLayer(tempLayerId);
                    if (map.getSource(tempSourceId)) map.removeSource(tempSourceId);
                    return;
                }
                if (map.getLayer(tempLayerId)) {
                    map.setPaintProperty(tempLayerId, 'line-opacity', blinkCount % 2 === 0 ? 1 : 0);
                }
                blinkCount++;
            }, 300);
        } catch (err) {
            // Silently fail
        }
    }, 1500);
}

/**
 * Очистить временные слои подсветки V2-зоны
 *
 * @param {Object} map - Mapbox GL JS map instance
 */
export function clearRestrictionZoneV2Highlight (map) {
    if (!map) return;
    try {
        if (map.getLayer('temp-v2-blink-layer')) map.removeLayer('temp-v2-blink-layer');
        if (map.getSource('temp-v2-blink-source')) map.removeSource('temp-v2-blink-source');
    } catch (err) {
        // Silently fail
    }
}
